import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import {
    DashboardPageHeader,
    CompactStatsRow,
    EmptyState,
} from '../../Components/Common/DashboardKit';
import AllTasks from './AllTasks';
import { useNavigate } from 'react-router-dom';
import api from "../../api";

/**
 * EPO landing page.
 *
 * Pass 2 UI update (2026):
 *  - Replaced the inline CountUp+avatar-title stat cards with the
 *    DashboardKit CompactStatsRow / StatTile. Same four metrics
 *    (Total, Pending, Approved, Void), same field shapes off the
 *    `summary` object.
 *  - Loading / error states now use EmptyState. Exact same trigger
 *    conditions as before (`loading`, `err`).
 *  - The data fetch (`api.get("Epo/all?type=ALL&empId=${userId}")`)
 *    and every piece of state (summary, loading, err) are unchanged.
 *    The empId still comes from sessionStorage authUser.uid. The
 *    AllTasks child component is rendered identically.
 */

/**
 * Map the EPO summary payload to StatTile-shaped objects.
 * Field names (total, pending, approved, deniedOrVoid) match the
 * backend response and MUST NOT be renamed.
 */
const buildTaskWidgets = (summary) => {
    if (!summary) {
        return [
            { key: "total", label: "Total EPOs", value: 0, icon: "ri-ticket-2-line", tone: "info", loading: true },
            { key: "pending", label: "Pending EPOs", value: 0, icon: "mdi mdi-timer-sand", tone: "warning", loading: true },
            { key: "approved", label: "Approved EPOs", value: 0, icon: "ri-checkbox-circle-line", tone: "success", loading: true },
            { key: "void", label: "Void EPOs", value: 0, icon: "ri-delete-bin-line", tone: "danger", loading: true },
        ];
    }
    return [
        {
            key: "total",
            label: "Total EPOs",
            value: summary.total || 0,
            icon: "ri-ticket-2-line",
            tone: "info",
        },
        {
            key: "pending",
            label: "Pending EPOs",
            value: summary.pending || 0,
            icon: "mdi mdi-timer-sand",
            tone: "warning",
        },
        {
            key: "approved",
            label: "Approved EPOs",
            value: summary.approved || 0,
            icon: "ri-checkbox-circle-line",
            tone: "success",
        },
        {
            key: "void",
            label: "Void EPOs",
            value: summary.deniedOrVoid || 0,
            icon: "ri-delete-bin-line",
            tone: "danger",
        },
    ];
};


const ExpensePurchaseOrder = () => {
    document.title = "EPO | SSW Technologies Inc";

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const userId = JSON.parse(sessionStorage.getItem("authUser"))?.uid;
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setErr(null);
                // adjust the URL if your controller route differs
                const res = await api.get(`Epo/all?type=ALL&empId=${userId}`);
                if (!alive) return;
                // R42 — summary debug log removed.
                setSummary(res.data.summary);
            } catch (e) {
                if (!alive) return;
                setErr(e?.response?.data ?? e?.message ?? "Failed to fetch summary");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // R29 — stat cards become clickable filters. Clicking a card
    // navigates to /epo?status=X. AllTasks reads the param and applies
    // it to the mineStatus dropdown.
    const navigate = useNavigate();
    const widgetData = useMemo(
        () => buildTaskWidgets(summary).map((w) => ({
            ...w,
            onClick: () => {
                const statusMap = {
                    total: "ALL",
                    pending: "PENDING",
                    approved: "APPROVED",
                    void: "DENIED",
                };
                const status = statusMap[w.key] || "ALL";
                navigate(`/epo?status=${status}`);
            },
        })),
        [summary, navigate]
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-ticket-2-line"
                        eyebrow="Procurement"
                        title="Expense Purchase Orders"
                        subtitle="Create, review, and track expense purchase orders through the approval pipeline."
                        tone="amber"
                        breadcrumb={[
                            { label: "Dashboard", to: "/" },
                            { label: "EPO" },
                        ]}
                    />

                    {err && !loading ? (
                        <Row className="mb-3">
                            <Col xs={12}>
                                <EmptyState
                                    icon="ri-error-warning-line"
                                    tone="danger"
                                    title="Could not load EPO summary"
                                    message={String(err)}
                                    compact
                                />
                            </Col>
                        </Row>
                    ) : (
                        <CompactStatsRow
                            stats={widgetData}
                            loading={loading}
                            columns={{ xxl: 3, sm: 6 }}
                            className="mb-3"
                        />
                    )}

                    <AllTasks />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ExpensePurchaseOrder;
